import { getIncomeById, IncomeType, Loan, OnboardingDetailsType } from '@nemuru-eng/js-api-client';

import { NetIncome } from '@/components/modules/DeclaredIncome/components/NetIncome';
import { NetIncomeEditDialog } from '@/components/modules/DeclaredIncome/components/NetIncomeEditDialog';
import { NonAvailableElementWarning } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/NonAvailableElementWarning';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { OnboardingDetails, PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';

interface DeclaredIncomeProps {
  loan: Loan;
  onboardingDetails: OnboardingDetails;
  editModeAllowed?: boolean;
}

export const DeclaredIncome = ({ loan, onboardingDetails, editModeAllowed }: DeclaredIncomeProps) => {
  const { opened, toggleDialog, setOpened } = useDialog();

  const onboardingDetailsId = onboardingDetails.id;

  if (!onboardingDetailsId) return <PendingDataWarning type={PendingDataType.BORROWER_FINANCIAL_INFORMATION} />;

  const {
    data: income,
    loading: loadingGetIncome,
    revalidate: revalidateGetIncome,
  } = useCache(
    getIncomeById,
    { loanId: loan.id, onboardingDetailsId },
    {
      fallbackData: undefined,
    },
  );

  const incomeId =
    onboardingDetails?.type === OnboardingDetailsType.BORROWER &&
    (onboardingDetails?.declaredIncomeId || onboardingDetails?.borrowerBankReaderId);

  const toggleEditModeDialog =
    editModeAllowed && income && income.type === IncomeType.DECLARED_INCOME ? toggleDialog : undefined;

  return (
    <SubSectionWrapper title={'NET_INCOME'} toggleEditModeDialog={toggleEditModeDialog}>
      {loadingGetIncome && <LoadingIncome />}
      {(!income || incomeId === null) && !loadingGetIncome && (
        <PendingDataWarning type={PendingDataType.BORROWER_FINANCIAL_INFORMATION} />
      )}
      {income && !loadingGetIncome && (
        <>
          <NetIncome income={income} />
          {opened && (
            <NetIncomeEditDialog
              income={income}
              opened={opened}
              setOpened={setOpened}
              toggleDialog={toggleDialog}
              refresh={() => revalidateGetIncome()}
            />
          )}
        </>
      )}
    </SubSectionWrapper>
  );
};

const LoadingIncome = () => (
  <NonAvailableElementWarning
    style={{
      fontSize: 20,
      minHeight: 110,
      height: 110,
    }}
    iconName={'schedule'}
    textId={'LOADING_INCOME'}
  />
);
